/**
 * applyFreeGiftOffer — global helper for free-gift-with-purchase CTAs.
 *
 *   applyFreeGiftOffer('FREEHAT115', {
 *     label: 'Free hat offer',  // copy: "Applying free hat offer…" / "Free hat offer added!"
 *     ui:    'toast',           // 'toast' (default) | 'modal'
 *   });
 *
 * Returns a Promise that resolves when the cart drawer is refreshed + opened,
 * or rejects if the discount can't be applied.
 *
 * Flow mirrors the theme's own add-to-cart and cart-discount paths:
 *   POST /cart/update.js with { discount, sections } → publish theme.pubsub
 *   cartUpdate → cart-items subscriber swaps MiniCart innerHTML → open drawer.
 */
(function () {
  'use strict';
  if (window.applyFreeGiftOffer) return;

  const SPINNER = '<span class="fgo-spinner" aria-hidden="true"></span>';
  const CHECK =
    '<svg class="fgo-icon" aria-hidden="true" viewBox="0 0 16 16" fill="none">' +
    '<path d="M3 8l3.5 3.5L13 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  const ALERT =
    '<svg class="fgo-icon" aria-hidden="true" viewBox="0 0 16 16" fill="none">' +
    '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M8 4v5M8 11.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  let stylesInjected = false;
  let inFlight = false;

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = [
      '.fgo-host { position: fixed; z-index: 9999; pointer-events: none; opacity: 0; transition: opacity 200ms ease; }',
      '.fgo-host[data-visible="true"] { opacity: 1; }',
      '.fgo-host[data-ui="toast"] { top: 24px; left: 50%; transform: translateX(-50%); }',
      '.fgo-host[data-ui="modal"] { inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.4); pointer-events: auto; }',
      '.fgo-card { font-family: var(--font-body-family, system-ui, -apple-system, sans-serif); font-size: 14px; line-height: 1.4; color: rgb(var(--color-foreground, 0 0 0)); background: rgb(var(--color-background, 255 255 255)); border-radius: 999px; padding: 12px 20px; box-shadow: 0 4px 24px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.06); display: flex; align-items: center; gap: 10px; pointer-events: auto; max-width: 90vw; transform: translateY(-8px); transition: transform 200ms ease; }',
      '.fgo-host[data-visible="true"] .fgo-card { transform: translateY(0); }',
      '.fgo-host[data-ui="modal"] .fgo-card { border-radius: 12px; padding: 20px 28px; font-size: 15px; transform: scale(0.96); }',
      '.fgo-host[data-ui="modal"][data-visible="true"] .fgo-card { transform: scale(1); }',
      '.fgo-card[data-state="error"] { background: rgb(var(--color-foreground, 0 0 0)); color: rgb(var(--color-background, 255 255 255)); }',
      '.fgo-spinner { width: 16px; height: 16px; border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%; animation: fgo-spin 600ms linear infinite; flex-shrink: 0; display: inline-block; }',
      '.fgo-icon { width: 16px; height: 16px; flex-shrink: 0; display: inline-block; }',
      '@keyframes fgo-spin { to { transform: rotate(360deg); } }',
      '@media (prefers-reduced-motion: reduce) { .fgo-host, .fgo-card { transition: none; } .fgo-spinner { animation: none; } }'
    ].join('\n');
    document.head.appendChild(style);
  }

  function createPopup(ui) {
    injectStyles();

    const host = document.createElement('div');
    host.className = 'fgo-host';
    host.setAttribute('data-ui', ui === 'modal' ? 'modal' : 'toast');
    host.setAttribute('data-visible', 'false');
    host.setAttribute('role', 'status');
    host.setAttribute('aria-live', 'polite');

    const card = document.createElement('div');
    card.className = 'fgo-card';
    card.setAttribute('data-state', 'loading');
    card.innerHTML = SPINNER + '<span class="fgo-text"></span>';
    host.appendChild(card);
    document.body.appendChild(host);

    const textEl = card.querySelector('.fgo-text');

    requestAnimationFrame(function () {
      host.setAttribute('data-visible', 'true');
    });

    function setIcon(html) {
      const old = card.querySelector('.fgo-spinner, .fgo-icon');
      if (old) old.outerHTML = html;
    }

    return {
      setLoading: function (msg) {
        card.setAttribute('data-state', 'loading');
        setIcon(SPINNER);
        textEl.textContent = msg;
      },
      setSuccess: function (msg) {
        card.setAttribute('data-state', 'success');
        setIcon(CHECK);
        textEl.textContent = msg;
      },
      setError: function (msg) {
        card.setAttribute('data-state', 'error');
        setIcon(ALERT);
        textEl.textContent = msg;
      },
      dismiss: function (delayMs) {
        setTimeout(function () {
          host.setAttribute('data-visible', 'false');
          setTimeout(function () { host.remove(); }, 250);
        }, delayMs || 0);
      }
    };
  }

  async function applyFreeGiftOffer(code, options) {
    if (!code || typeof code !== 'string') {
      throw new Error('applyFreeGiftOffer: code is required');
    }
    if (inFlight) return;
    inFlight = true;

    const opts = options || {};
    const label = opts.label || 'Free gift offer';
    const ui = opts.ui === 'modal' ? 'modal' : 'toast';

    const popup = createPopup(ui);
    popup.setLoading('Applying ' + label + '…');

    try {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) throw new Error('cart-drawer element not found');
      const sectionId = drawer.getAttribute('data-section-id');
      if (!sectionId) throw new Error('cart-drawer missing data-section-id');

      // Read existing discount codes so we don't clobber them.
      const currentCart = await fetch('/cart.js', {
        credentials: 'same-origin'
      }).then(function (r) { return r.json(); });

      const existing = (currentCart.discount_codes || [])
        .map(function (d) { return (d.code || '').trim(); })
        .filter(Boolean);

      const target = code.toUpperCase();
      const alreadyApplied = existing.some(function (c) {
        return c.toUpperCase() === target;
      });

      // Atomic: apply discount + render cart drawer section in one round trip.
      // If already applied, sending the same list is idempotent and still
      // returns fresh section HTML.
      const merged = alreadyApplied
        ? existing.join(',')
        : existing.concat([code]).join(',');

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: merged,
          sections: [sectionId]
        })
      });
      const parsedState = await res.json();

      const matched = (parsedState.discount_codes || []).find(function (d) {
        return (d.code || '').toUpperCase() === target;
      });
      if (!matched) {
        // Code completely missing from response → invalid / expired / typo.
        throw new Error('discount-not-found');
      }

      // Publish through the theme's own pubsub. cart-items.onCartUpdate
      // swaps MiniCart innerHTML (using parsedState.sections) and dispatches
      // cart:updated, which the gift system observes.
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'free-gift-offer',
          cart: parsedState
        });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true }
        }));
      }

      drawer.show();

      // applicable:false = code is on the cart but the cart doesn't yet
      // meet the discount's minimum (subtotal/quantity/product).
      if (matched.applicable === false) {
        popup.setSuccess('Offer applied to cart!');
        popup.dismiss(1500);
      } else if (alreadyApplied) {
        popup.setSuccess(label + ' is already in your cart');
        popup.dismiss(1500);
      } else {
        popup.setSuccess(label + ' added!');
        popup.dismiss(1500);
      }
    } catch (err) {
      popup.setError('Something went wrong, please try again.');
      popup.dismiss(3000);
      throw err;
    } finally {
      inFlight = false;
    }
  }

  window.applyFreeGiftOffer = applyFreeGiftOffer;
})();
